import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar, Users, Target, TrendingUp, Code, Brain, Rocket, CheckCircle, ArrowRight, Award, Lightbulb, Zap } from "lucide-react";

const AboutUs = () => {
  const milestones = [
    {
      year: "2016",
      title: "Los inicios",
      description: "Fundamos Innova2Market con la visión de crear soluciones de software a medida que transformaran la forma de trabajar de las empresas.",
      icon: <Code className="h-6 w-6 text-blue-600" />,
      achievements: [
        "Primeros proyectos de desarrollo web",
        "Especialización en sistemas CRM",
        "Equipo de 3 desarrolladores"
      ]
    },
    {
      year: "2018-2020",
      title: "Expansión y crecimiento",
      description: "Ampliamos nuestro equipo y diversificamos nuestros servicios, convirtiéndonos en referentes en desarrollo de software empresarial.",
      icon: <Users className="h-6 w-6 text-green-600" />,
      achievements: [
        "Crecimiento a 8 desarrolladores",
        "Más de 50 proyectos completados",
        "Especialización en e-commerce"
      ]
    },
    {
      year: "2021-2022",
      title: "Innovación tecnológica",
      description: "Comenzamos a integrar tecnologías emergentes y automatizaciones básicas en nuestros proyectos, preparando el terreno para la revolución de la IA.",
      icon: <Lightbulb className="h-6 w-6 text-yellow-600" />,
      achievements: [
        "Primeras automatizaciones con bots",
        "Integración de APIs avanzadas",
        "Equipo de 12 profesionales"
      ]
    },
    {
      year: "2023",
      title: "La revolución de la IA",
      description: "Nos especializamos completamente en inteligencia artificial y automatizaciones, transformando nuestra empresa para liderar la nueva era digital.",
      icon: <Brain className="h-6 w-6 text-purple-600" />,
      achievements: [
        "Especialización total en IA",
        "Primeros asistentes telefónicos",
        "Más de 100 automatizaciones implementadas"
      ]
    }
  ];

  const values = [
    {
      icon: <Target className="h-8 w-8 text-blue-600" />,
      title: "Innovación constante",
      description: "Siempre a la vanguardia de la tecnología, explorando nuevas formas de automatizar y optimizar procesos empresariales."
    },
    {
      icon: <Users className="h-8 w-8 text-green-600" />,
      title: "Enfoque centrado en el cliente",
      description: "Cada solución se diseña pensando en las necesidades específicas de nuestros clientes y sus objetivos de negocio."
    },
    {
      icon: <Zap className="h-8 w-8 text-yellow-600" />,
      title: "Resultados medibles",
      description: "Nos enfocamos en entregar soluciones que generen un impacto real y cuantificable en la productividad y rentabilidad."
    },
    {
      icon: <Award className="h-8 w-8 text-purple-600" />,
      title: "Calidad y excelencia",
      description: "Mantenemos los más altos estándares de calidad en cada proyecto, desde la concepción hasta la implementación."
    }
  ];

  const teamStats = [
    { number: "8", label: "Años de experiencia", icon: <Calendar className="h-6 w-6" /> },
    { number: "15+", label: "Profesionales especializados", icon: <Users className="h-6 w-6" /> },
    { number: "200+", label: "Proyectos completados", icon: <Target className="h-6 w-6" /> },
    { number: "100%", label: "Clientes satisfechos", icon: <CheckCircle className="h-6 w-6" /> }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Hero Section */}
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-sm text-blue-600 mb-6">
            <Award className="w-4 h-4 mr-2" />
            Nuestra Historia
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-800 mb-6">
            De desarrolladores a{" "}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              especialistas en IA
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
            Desde 2016, hemos evolucionado de ser una empresa de desarrollo de software a medida 
            a convertirnos en líderes en automatización e inteligencia artificial. 
            Nuestra experiencia nos permite crear soluciones que realmente transforman negocios.
          </p>
        </div>

        {/* Team Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-16 sm:mb-20">
          {teamStats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl mx-auto mb-3 sm:mb-4">
                <div className="text-purple-600">{stat.icon}</div>
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">{stat.number}</div>
              <div className="text-sm sm:text-base text-gray-600 font-medium px-2">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="mb-16 sm:mb-20">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 text-center mb-8 sm:mb-12">
            Nuestra evolución
          </h2>
          
          <div className="relative">
            {/* Timeline line - hidden on mobile */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-purple-200 to-blue-200 rounded-full"></div>
            
            <div className="space-y-8 sm:space-y-12 lg:space-y-16">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative">
                  {/* Mobile layout */}
                  <div className="lg:hidden">
                    <Card className="p-6 sm:p-8 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl mr-4">
                          {milestone.icon}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-purple-600">{milestone.year}</div>
                          <h3 className="text-lg sm:text-xl font-bold text-gray-800">{milestone.title}</h3>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-4 leading-relaxed text-sm sm:text-base">
                        {milestone.description}
                      </p>
                      
                      <ul className="space-y-2">
                        {milestone.achievements.map((achievement, achievementIndex) => (
                          <li key={achievementIndex} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </div>

                  {/* Desktop layout */}
                  <div className={`hidden lg:flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    {/* Content */}
                    <div className={`flex-1 ${index % 2 === 0 ? 'pr-8 lg:pr-16' : 'pl-8 lg:pl-16'}`}>
                      <Card className="p-8 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center mb-4">
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl mr-4">
                            {milestone.icon}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-purple-600">{milestone.year}</div>
                            <h3 className="text-xl font-bold text-gray-800">{milestone.title}</h3>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {milestone.description}
                        </p>
                        
                        <ul className="space-y-2">
                          {milestone.achievements.map((achievement, achievementIndex) => (
                            <li key={achievementIndex} className="flex items-center text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    </div>
                    
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full border-4 border-white shadow-lg z-10"></div>
                    
                    {/* Spacer */}
                    <div className="flex-1"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16 sm:mb-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Nuestros valores
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Los principios que nos han guiado desde el primer día y que siguen 
              definiendo cómo trabajamos y nos relacionamos con nuestros clientes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <Card key={index} className="p-6 text-center bg-white hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-100 to-white rounded-2xl mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Mission Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-white text-center mb-16 sm:mb-20">
          <Rocket className="h-12 w-12 sm:h-16 sm:w-16 text-white mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
            Nuestra misión
          </h2>
          <p className="text-lg sm:text-xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 px-4">
            Democratizar el acceso a la inteligencia artificial para empresas de todos los tamaños, 
            creando soluciones que no solo automatizan procesos, sino que generan valor real y 
            sostenible para nuestros clientes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => window.open('https://cal.com/peter-gal/30min?overlayCalendar=true', '_blank')}
              className="bg-white text-purple-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Calendar className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Conoce nuestro equipo
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => window.location.href = '/casos-exito'}
              className="border-white text-white hover:bg-white hover:text-purple-600 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-base sm:text-lg"
            >
              <TrendingUp className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Ver nuestros resultados
            </Button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AboutUs;
